import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Shield, Eye, Lock, UserCheck, Mail, Phone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { SEO } from '../components/SEO';

export const DatenschutzPage = () => {
  const navigate = useNavigate();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEO 
        title="Datenschutzerklärung"
        description="Datenschutzerklärung von MP Frankendach GmbH - Informationen zur Verarbeitung personenbezogener Daten gemäß DSGVO"
        canonical="https://mp-frankendach.de/datenschutz"
        noindex={true}
      />
      
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white shadow-sm">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <motion.button
                onClick={() => navigate('/')}
                className="flex items-center space-x-2 text-gray-600 hover:text-red-600 transition-colors"
                whileHover={{ x: -5 }}
              >
                <ArrowLeft size={24} />
                <span className="font-semibold">Zurück</span>
              </motion.button>
              <img
                src="/images/logo.png"
                alt="MP Frankendach Logo"
                className="h-12 w-auto object-contain"
              />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-8 text-gray-900">
              Datenschutzerklärung
            </h1>

            {/* Trust Badges */}
            <div className="grid md:grid-cols-3 gap-4 mb-12">
              <PrivacyBadge icon={<Eye />} text="Transparent" />
              <PrivacyBadge icon={<Lock />} text="Sicher" />
              <PrivacyBadge icon={<UserCheck />} text="Ihre Rechte" />
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8 space-y-8">
              {/* 1. Verantwortlicher */}
              <section>
                <h2 className="text-2xl font-bold mb-4 text-gray-900">1. Verantwortlicher</h2>
                <div className="bg-gray-50 rounded-lg p-6">
                  <p className="font-semibold text-lg text-gray-900 mb-3">MP Frankendach GmbH</p>
                  <div className="space-y-2 text-gray-700">
                    <p>Schwabacher Str. 260</p>
                    <p>90763 Fürth</p>
                    <p>Deutschland</p>
                    <p className="mt-4 font-semibold">Vertreten durch die Geschäftsführerin:</p>
                    <p>Antesa Strana Peci</p>
                    <div className="mt-4 space-y-2">
                      <div className="flex items-center space-x-2">
                        <Phone size={16} className="text-red-600" />
                        <a href="tel:+4991198012850" className="text-red-600 hover:underline">0911 - 980 12 850</a>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Mail size={16} className="text-red-600" />
                        <a href="mailto:info@mp-frankendach.de" className="text-red-600 hover:underline">info@mp-frankendach.de</a>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* 2. Allgemeine Hinweise */}
              <section>
                <h2 className="text-2xl font-bold mb-4 text-gray-900">2. Allgemeine Hinweise zur Datenverarbeitung</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Wir verarbeiten personenbezogene Daten ausschließlich im Rahmen der gesetzlichen Bestimmungen der Datenschutz-Grundverordnung (DSGVO) sowie des Bundesdatenschutzgesetzes (BDSG).
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Personenbezogene Daten sind alle Daten, mit denen Sie persönlich identifiziert werden können.
                </p>
              </section>

              {/* 3. Hosting */}
              <section>
                <h2 className="text-2xl font-bold mb-4 text-gray-900">3. Hosting</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Diese Website wird bei <strong>Vercel Inc.</strong><br />
                  340 S Lemon Ave #4133<br />
                  Walnut, CA 91789<br />
                  USA<br />
                  gehostet.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Die Server befinden sich primär in der Europäischen Union. Es kann jedoch nicht ausgeschlossen werden, dass im Rahmen von Support-, Wartungs- oder Administrationsleistungen ein Zugriff aus den USA erfolgt.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Mit dem Hosting-Anbieter wurde ein Vertrag zur Auftragsverarbeitung gemäß Art. 28 DSGVO abgeschlossen.
                </p>
                <div className="bg-red-50 border-l-4 border-red-600 p-4">
                  <p className="text-gray-800 font-semibold mb-2">Datenübermittlung in die USA</p>
                  <p className="text-gray-700 text-sm">
                    Soweit eine Datenübermittlung in die USA erfolgt, geschieht dies auf Grundlage von:
                  </p>
                  <ul className="list-disc list-inside text-gray-700 text-sm mt-2 space-y-1">
                    <li>Standardvertragsklauseln der EU-Kommission</li>
                    <li>ggf. Zertifizierung nach dem EU-U.S. Data Privacy Framework</li>
                  </ul>
                </div>
                <p className="text-gray-700 leading-relaxed mt-4">
                  <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an sicherem und effizientem Hosting).
                </p>
              </section>

              {/* 4. Server-Logfiles */}
              <section>
                <h2 className="text-2xl font-bold mb-4 text-gray-900">4. Server-Logfiles</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Beim Aufruf dieser Website werden automatisch folgende Daten erfasst:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-1 mb-4">
                  <li>IP-Adresse</li>
                  <li>Datum und Uhrzeit der Anfrage</li>
                  <li>Browsertyp und Browserversion</li>
                  <li>Betriebssystem</li>
                  <li>Referrer-URL</li>
                  <li>Hostname des zugreifenden Rechners</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Diese Daten dienen der technischen Bereitstellung sowie der Sicherheit und Stabilität der Website.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. f DSGVO.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  <strong>Speicherdauer:</strong> Die Speicherung erfolgt für die Dauer, die zur Gewährleistung der Sicherheit und Stabilität der Website erforderlich ist. Eine längere Speicherung erfolgt nur, sofern dies gesetzlich vorgeschrieben ist.
                </p>
              </section>

              {/* 5. Kontaktformular */}
              <section>
                <h2 className="text-2xl font-bold mb-4 text-gray-900">5. Kontaktformular</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Wenn Sie uns per Kontaktformular Anfragen senden, werden folgende Daten verarbeitet:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-1 mb-4">
                  <li>Name</li>
                  <li>E-Mail-Adresse</li>
                  <li>Telefonnummer</li>
                  <li>Nachricht</li>
                  <li>ggf. Betreff</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Die Verarbeitung erfolgt zur Bearbeitung Ihrer Anfrage.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  <strong>Rechtsgrundlage:</strong><br />
                  Art. 6 Abs. 1 lit. b DSGVO (vorvertragliche Maßnahmen)<br />
                  bzw. Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an Kommunikation)
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Die Daten werden nicht ohne Ihre Einwilligung weitergegeben.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  <strong>Speicherdauer:</strong> Bis zur abschließenden Bearbeitung der Anfrage sowie darüber hinaus entsprechend gesetzlicher Aufbewahrungsfristen.
                </p>
              </section>

              {/* 6. E-Mail-Versand */}
              <section>
                <h2 className="text-2xl font-bold mb-4 text-gray-900">6. E-Mail-Versand über Resend</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Zur Übermittlung von Formularanfragen nutzen wir den Dienst <strong>Resend Inc.</strong><br />
                  2261 Market Street #5039<br />
                  San Francisco, CA 94114<br />
                  USA
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Die Daten werden ausschließlich zur Zustellung der E-Mail verarbeitet.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Mit dem Anbieter wurde ein Vertrag zur Auftragsverarbeitung gemäß Art. 28 DSGVO abgeschlossen.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Da es sich um ein US-Unternehmen handelt, kann eine Übermittlung personenbezogener Daten in die USA erfolgen. Diese erfolgt auf Grundlage von Standardvertragsklauseln der EU-Kommission sowie ggf. weiterer geeigneter Garantien (z. B. EU-U.S. Data Privacy Framework).
                </p>
                <p className="text-gray-700 leading-relaxed">
                  <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. b DSGVO.
                </p>
              </section>

              {/* 7. Social Media */}
              <section>
                <h2 className="text-2xl font-bold mb-4 text-gray-900">7. Social-Media-Links</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Auf unserer Website befinden sich Links zu unseren Profilen bei Instagram, Facebook und LinkedIn.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Es handelt sich hierbei ausschließlich um externe Links. Beim Besuch unserer Website werden keine Daten automatisch an diese Plattformen übertragen.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Erst durch Anklicken des jeweiligen Links wird eine Verbindung zur entsprechenden Plattform hergestellt.
                </p>
              </section>

              {/* 8. Cookies */}
              <section>
                <h2 className="text-2xl font-bold mb-4 text-gray-900">8. Cookies</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Diese Website verwendet ausschließlich technisch notwendige Cookies, die für den Betrieb der Website erforderlich sind.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  <strong>Rechtsgrundlage:</strong> § 25 Abs. 2 TTDSG i.V.m. Art. 6 Abs. 1 lit. f DSGVO.
                </p>
              </section>

              {/* 9. Ihre Rechte */}
              <section>
                <h2 className="text-2xl font-bold mb-4 text-gray-900">9. Ihre Rechte</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Sie haben jederzeit das Recht auf:
                </p>
                <div className="bg-red-50 border-l-4 border-red-600 p-4 mb-4">
                  <ul className="list-disc list-inside text-gray-700 space-y-1">
                    <li>Auskunft gemäß Art. 15 DSGVO</li>
                    <li>Berichtigung gemäß Art. 16 DSGVO</li>
                    <li>Löschung gemäß Art. 17 DSGVO</li>
                    <li>Einschränkung der Verarbeitung gemäß Art. 18 DSGVO</li>
                    <li>Datenübertragbarkeit gemäß Art. 20 DSGVO</li>
                    <li>Widerspruch gemäß Art. 21 DSGVO</li>
                  </ul>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  <strong>Beschwerderecht bei der zuständigen Aufsichtsbehörde:</strong>
                </p>
                <div className="bg-gray-50 rounded-lg p-6">
                  <p className="font-semibold text-gray-900 mb-2">Bayerisches Landesamt für Datenschutzaufsicht (BayLDA)</p>
                  <div className="space-y-1 text-gray-700">
                    <p>Promenade 27</p>
                    <p>91522 Ansbach</p>
                    <p>Deutschland</p>
                    <p className="mt-2">Telefon: +49 (0)981 180093-0</p>
                    <p>E-Mail: <a href="mailto:poststelle@lda.bayern.de" className="text-red-600 hover:underline">poststelle@lda.bayern.de</a></p>
                  </div>
                </div>
              </section>

              {/* 10. Speicherdauer */}
              <section>
                <h2 className="text-2xl font-bold mb-4 text-gray-900">10. Speicherdauer allgemein</h2>
                <p className="text-gray-700 leading-relaxed">
                  Personenbezogene Daten werden nur so lange gespeichert, wie dies für den jeweiligen Zweck erforderlich ist oder gesetzliche Aufbewahrungsfristen bestehen.
                </p>
              </section>

              {/* 11. SSL */}
              <section>
                <h2 className="text-2xl font-bold mb-4 text-gray-900">11. SSL- bzw. TLS-Verschlüsselung</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Diese Website nutzt aus Sicherheitsgründen und zum Schutz der Übertragung vertraulicher Inhalte, wie zum Beispiel Anfragen über das Kontaktformular, eine SSL- bzw. TLS-Verschlüsselung.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Eine verschlüsselte Verbindung erkennen Sie daran, dass die Adresszeile des Browsers von „http://" auf „https://" wechselt und an dem Schloss-Symbol in Ihrer Browserzeile.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Wenn die SSL- bzw. TLS-Verschlüsselung aktiviert ist, können die Daten, die Sie an uns übermitteln, nicht von Dritten mitgelesen werden.
                </p>
              </section>
            </div>

            <p className="text-sm text-gray-500 mt-8 text-center">
              Stand: Dezember 2024
            </p>
          </motion.div>
        </div>
      </div>
    </>
  );
};

const PrivacyBadge = ({ icon, text }) => (
  <motion.div
    className="flex flex-col items-center text-center p-4 bg-white rounded-xl shadow-md"
    whileHover={{ y: -5, boxShadow: '0 10px 30px rgba(220, 38, 38, 0.2)' }}
  >
    <div className="p-3 bg-gradient-to-br from-red-600 to-red-800 rounded-full mb-2">
      {React.cloneElement(icon, { size: 24, className: 'text-white' })}
    </div>
    <span className="text-sm font-semibold text-gray-900">{text}</span>
  </motion.div>
);